import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authApi } from '@/services/api';
import { getErrorMessage } from '@/services/apiClient';
import { mapStaffAuthUser } from '@/lib/mapStaffAuthUser';
import { useAuthStore } from '@/store/authStore';

export function CorporateAuthCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const storeLogin = useAuthStore((s) => s.login);
  const [error, setError] = useState<string | null>(null);

  const code = searchParams.get('code');
  const nextPath = useMemo(() => {
    const next = searchParams.get('next');
    return next && next.startsWith('/') ? next : '/';
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;

    async function exchangeCode() {
      if (!code) {
        setError('Missing corporate login code.');
        return;
      }

      try {
        const response = await authApi.exchangeCorporateCode(code);
        if (cancelled) {
          return;
        }

        storeLogin(
          response.token,
          mapStaffAuthUser(response.user),
          response.pendingDisclaimers ?? [],
          response.modules ?? null,
          response.ownerAgreementHold ?? false,
        );
        // RequireAuth diverts to /disclaimer when the queue is non-empty, and
        // to /owner-agreement-required while the practice is waiting for its
        // first owner to accept.
        navigate(nextPath, { replace: true });
      } catch (err: unknown) {
        if (!cancelled) {
          setError(getErrorMessage(err, 'Corporate login failed. Please try again.'));
        }
      }
    }

    void exchangeCode();

    return () => {
      cancelled = true;
    };
  }, [code, navigate, nextPath, storeLogin]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 text-center shadow-sm">
        <h1 className="text-xl font-semibold text-slate-900">Signing you in</h1>
        {error ? (
          <>
            <p className="mt-3 text-sm text-red-600">{error}</p>
            <button
              type="button"
              onClick={() => navigate('/login?form=1', { replace: true })}
              className="mt-5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90"
            >
              Back to login
            </button>
          </>
        ) : (
          <p className="mt-3 text-sm text-slate-500">Completing your corporate authentication...</p>
        )}
      </div>
    </div>
  );
}
